import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { SiroccoApiError } from '../../api/client';
import { auth as authApi } from '../../api/endpoints';
import { SubScreen } from '../../components/layout/Screens';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { useSession } from '../../store/SessionContext';
import { t } from '../../i18n';

/**
 * Adresse e-mail et mot de passe.
 *
 * L'adresse est rappelee en tete, en lecture seule : c'est l'identifiant
 * du compte, celui avec lequel on se connecte, et l'endroit ou l'on vient
 * naturellement le chercher. La changer est une autre affaire — Firebase
 * demande de reverifier la nouvelle adresse — et rien ne le propose
 * aujourd'hui.
 *
 * Les memes trois regles qu'a l'inscription s'allument a la saisie, et
 * Firebase redemande le mot de passe actuel : c'est lui qui verifie qu'on
 * est bien devant son propre compte, pas l'application.
 */
export function PasswordScreen() {
  const navigate = useNavigate();
  // L'adresse vient de la session : elle y est deja, inutile de la relire.
  const { user } = useSession();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [field, setField] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [oubli, setOubli] = useState<'repos' | 'en-cours' | 'parti' | 'echec'>('repos');

  async function envoyerLeLien() {
    if (!user?.email) return;
    setOubli('en-cours');
    try {
      await authApi.forgotPassword(user.email);
      setOubli('parti');
    } catch {
      setOubli('echec');
    }
  }

  const rules = useMemo(
    () => [
      { label: t.auth.inscription.regles.huit, ok: next.length >= 8 },
      { label: t.auth.inscription.regles.casse, ok: /[a-z]/.test(next) && /[A-Z]/.test(next) },
      {
        label: t.auth.inscription.regles.chiffre,
        ok: /[0-9]/.test(next) || /[^A-Za-z0-9]/.test(next),
      },
    ],
    [next],
  );

  const solid = rules.every((rule) => rule.ok);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setField(null);

    if (!solid) {
      setError(t.profil.motDePasse.troisConditions);
      setField('next');
      return;
    }
    if (next !== confirm) {
      setError(t.profil.motDePasse.pasIdentiques);
      setField('confirm');
      return;
    }

    setPending(true);
    try {
      await authApi.changePassword(current, next);
      navigate('/app/profile/settings', { replace: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.profil.motDePasse.impossible);
      if (cause instanceof SiroccoApiError && cause.field) setField(cause.field);
      else if ((cause as { field?: string })?.field) setField((cause as { field: string }).field);
    } finally {
      setPending(false);
    }
  }

  return (
    <SubScreen
      title={t.profil.motDePasse.titre}
      bottomBar={
        <Button
          block
          loading={pending}
          disabled={!current || !next || !confirm}
          onClick={(event) => void onSubmit(event)}
        >
          {t.profil.motDePasse.changer}
        </Button>
      }
    >
      <div className="pr-mail">
        <p className="pr-mail__label">{t.auth.champs.email}</p>
        <p className="pr-mail__value">{user?.email}</p>
      </div>

      <form className="pr-password" onSubmit={onSubmit}>
        <Field
          tone="spot"
          label={t.profil.motDePasse.actuel}
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
          error={field === 'current' ? error : null}
        />

        <Field
          tone="spot"
          label={t.profil.motDePasse.nouveau}
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(event) => setNext(event.target.value)}
          error={field === 'next' ? error : null}
        />

        <ul className="auth__rules pr-password__rules">
          {rules.map((rule) => (
            <li key={rule.label} className={rule.ok ? 'auth__rule auth__rule--ok' : 'auth__rule'}>
              <span className="auth__bullet" />
              {rule.label}
            </li>
          ))}
        </ul>

        <Field
          tone="spot"
          label={t.profil.motDePasse.confirmer}
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          error={field === 'confirm' ? error : null}
        />

        {error && !field && <p className="field__error">{error}</p>}

        {/*
          Pour qui ne se souvient plus de l'actuel.

          Discret, en bas a gauche : c'est une sortie de secours, pas la
          voie normale — la voie normale est le formulaire au-dessus.
          L'adresse est celle de la session, donc rien a saisir : le
          courriel part vers la boite dont on est deja connecte.
        */}
        <button
          type="button"
          className="pr-password__oubli"
          disabled={oubli !== 'repos'}
          onClick={() => void envoyerLeLien()}
        >
          {oubli === 'parti' ? t.profil.motDePasse.envoye : t.auth.connexion.oublie}
        </button>

        {oubli === 'parti' && (
          <p className="pr-password__oubli-mot">
            {t.profil.motDePasse.lienRecu(user?.email ?? '')}
          </p>
        )}
        {oubli === 'echec' && <p className="field__error">{t.auth.confirmer.echec}</p>}
      </form>
    </SubScreen>
  );
}
